import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import Login from "@/pages/Login";
import VerifyStaff from "@/pages/VerifyStaff";
import SuperAdminLogin from "@/pages/SuperAdminLogin";
import SuperAdminForgotPassword from "@/pages/SuperAdminForgotPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import EmployeeDetail from "@/pages/EmployeeDetail";
import Departments from "@/pages/Departments";
import Leaves from "@/pages/Leaves";
import Users from "@/pages/Users";
import PayrollList, { PayrollDetail } from "@/pages/Payroll";
import Loans from "@/pages/Loans";
import Reports from "@/pages/Reports";
import Attendance from "@/pages/Attendance";
import Settlements from "@/pages/Settlements";
import Settings from "@/pages/Settings";
import Performance from "@/pages/Performance";
import Backups from "@/pages/Backups";
import ActivityLog from "@/pages/ActivityLog";
import Companies from "@/pages/Companies";
import DatabaseAdmin from "@/pages/DatabaseAdmin";
import Documents from "@/pages/Documents";
import PendingApprovals from "@/pages/PendingApprovals";
import EmployeeSignup from "@/pages/EmployeeSignup";
import AttendanceCheck from "@/pages/AttendanceCheck";
import Worksites from "@/pages/Worksites";
import MyDocuments from "@/pages/MyDocuments";
import PublicUpload from "@/pages/PublicUpload";
import PayslipReport from "@/pages/PayslipReport";
import LoanLedgerReport from "@/pages/LoanLedgerReport";
import MyRequests from "@/pages/MyRequests";
import MyPerformance from "@/pages/MyPerformance";
import Memos from "@/pages/Memos";
import RecurringDeductions from "@/pages/RecurringDeductions";
import DailyTasks from "@/pages/DailyTasks";
import Offsite from "@/pages/Offsite";
import ChangePassword from "@/pages/ChangePassword";
import ThemeProvider from "@/components/ThemeProvider";
import { ThemeProvider as DarkModeProvider } from "@/context/ThemeContext";
import { ConfirmProvider } from "@/components/ConfirmProvider";
import { Toaster } from "@/components/ui/sonner";
import PayChangeRequests from "@/pages/PayChangeRequests";
import Chat from "@/pages/Chat";
import AiAssistant from "@/pages/AiAssistant";
import FaceEnroll from "@/pages/FaceEnroll";
import EmployeeApprovals from "@/pages/EmployeeApprovals";
import MissingDepartments from "@/pages/MissingDepartments";
import BulkDivisions from "@/pages/BulkDivisions";
import Activate from "@/pages/Activate";
import Billing from "@/pages/Billing";
import BillingCallback from "@/pages/BillingCallback";
import SuperAdminBilling from "@/pages/SuperAdminBilling";
import SuperAdminRevenue from "@/pages/SuperAdminRevenue";
import SuperAdminSupport from "@/pages/SuperAdminSupport";

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <ThemeProvider>
                    <DarkModeProvider>
                    <ConfirmProvider>
                    <BrowserRouter>
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/verify" element={<VerifyStaff />} />
                        <Route path="/verify/:token" element={<VerifyStaff />} />
                        <Route path="/activate/:token" element={<Activate />} />
                        <Route path="/signup" element={<EmployeeSignup />} />
                        <Route path="/super-admin/login" element={<SuperAdminLogin />} />
                        <Route path="/super-admin/forgot-password" element={<SuperAdminForgotPassword />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route
                            path="/change-password"
                            element={
                                <ProtectedRoute>
                                    <ChangePassword />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/upload-documents/:token" element={<PublicUpload />} />
                        <Route
                            path="/companies"
                            element={
                                <ProtectedRoute requireSuperAdmin={true}>
                                    <SuperAdminLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<Companies />} />
                        </Route>
                        <Route
                            path="/super-admin/billing"
                            element={
                                <ProtectedRoute requireSuperAdmin={true}>
                                    <SuperAdminLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<SuperAdminBilling />} />
                        </Route>
                        <Route
                            path="/super-admin/revenue"
                            element={
                                <ProtectedRoute requireSuperAdmin={true}>
                                    <SuperAdminLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<SuperAdminRevenue />} />
                        </Route>
                        <Route
                            path="/super-admin/support"
                            element={
                                <ProtectedRoute requireSuperAdmin={true}>
                                    <SuperAdminLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<SuperAdminSupport />} />
                        </Route>
                        <Route
                            path="/super-admin/database"
                            element={
                                <ProtectedRoute requireSuperAdmin={true}>
                                    <SuperAdminLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<DatabaseAdmin />} />
                        </Route>
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute>
                                    <AppLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<Navigate to="/dashboard" replace />} />
                            <Route path="dashboard" element={<Dashboard />} />
                            <Route path="employees" element={<Employees />} />
                            <Route path="employees/:id" element={<EmployeeDetail />} />
                            <Route path="departments" element={<Departments />} />
                            <Route path="leaves" element={<Leaves />} />
                            <Route path="payroll" element={<PayrollList />} />
                            <Route path="payroll/:id" element={<PayrollDetail />} />
                            <Route path="loans" element={<Loans />} />
                            <Route path="attendance" element={<Attendance />} />
                            <Route path="settlements" element={<Settlements />} />
                            <Route path="settlements/:id" element={<Settlements />} />
                            <Route path="performance" element={<Performance />} />
                            <Route path="reports" element={<Reports />} />
                            <Route path="users" element={<Users />} />
                            <Route path="settings" element={<Settings />} />
                            <Route path="backups" element={<Backups />} />
                            <Route path="activity-log" element={<ActivityLog />} />
                            <Route path="pay-change-requests" element={<PayChangeRequests />} />
                            <Route path="bulk-divisions" element={<BulkDivisions />} />
                            <Route path="billing" element={<Billing />} />
                            <Route path="billing/callback" element={<BillingCallback />} />
                            <Route path="missing-departments" element={<MissingDepartments />} />
                            <Route path="documents" element={<Documents />} />
                            <Route path="pending-approvals" element={<PendingApprovals />} />
                            <Route path="check-in" element={<AttendanceCheck />} />
                            <Route path="worksites" element={<Worksites />} />
                            <Route path="my-documents" element={<MyDocuments />} />
                            <Route path="my/payslips" element={<PayslipReport selfOnly={true} />} />
                            <Route path="my/loan-ledger" element={<LoanLedgerReport selfOnly={true} />} />
                            <Route path="my/requests" element={<MyRequests />} />
                            <Route path="my/performance" element={<MyPerformance />} />
                            <Route path="reports/payslips" element={<PayslipReport selfOnly={false} />} />
                            <Route path="reports/loan-ledger" element={<LoanLedgerReport selfOnly={false} />} />
                            <Route path="memos" element={<Memos />} />
                            <Route path="recurring-deductions" element={<RecurringDeductions />} />
                            <Route path="tasks" element={<DailyTasks />} />
                            <Route path="offsite" element={<Offsite />} />
                            <Route path="chat" element={<Chat />} />
                            <Route path="ai" element={<AiAssistant />} />
                            <Route path="face-enroll" element={<FaceEnroll />} />
                            <Route path="employee-approvals" element={<EmployeeApprovals />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
                <Toaster position="top-center" richColors />
                    </ConfirmProvider>
                    </DarkModeProvider>
                </ThemeProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
